import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaSearch, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { COLORS } from '../../../constants/radar';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import EmployeeSearch from '../../common/EmployeeSearch';
import { useAlert } from '../../../contexts/AlertContext';
import Modal from '../../common/Modal';
import ConfirmationModal from '../../common/ConfirmationModal';
import EmptyState from '../../common/EmptyState';
import LoadingSpinner from '../../common/LoadingSpinner';
import { permissionsApi } from '../../../services/api/permissionsApi';

// Import the same card styles from Roles
import { MainContainer, RolesGrid, RoleCard, RoleCardContent, RoleDetailsGrid, RoleButtons } from '../Roles/styles';

const EmployeePermissionOverrides = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Load employee permission overrides on component mount
  useEffect(() => {
    loadEmployeePermissionOverrides();
  }, []);

  const loadEmployeePermissionOverrides = useCallback(async () => {
    try {
      setLoading(true);
      const overrides = await permissionsApi.getAllEmployeePermissionOverrides();
      setEmployees(overrides);
    } catch (error) {
      console.error('Error loading employee permission overrides:', error);
      showAlert(error.message || 'Failed to load individual permission overrides', 'error');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const filteredEmployees = employees.filter(employeeOverride =>
    employeeOverride.employee.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employeeOverride.employee.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get already assigned employee IDs
  const assignedEmployeeIds = employees.map(emp => emp.employee.id);

  const handleOpenAssignModal = () => {
    setModalOpen(true);
    setSelectedEmployee(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleNavigateToEmployee = (employeeId) => {
    navigate(`/permissions/employee-overrides/${employeeId}`);
  };

  const handleAddOverride = async () => {
    if (!selectedEmployee) {
      showAlert('Please select an employee', 'error');
      return;
    }

    // Navigate to the employee override details page
    navigate(`/permissions/employee-overrides/${selectedEmployee.id}`);
    handleCloseModal();
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions')}>
            <FaArrowLeft /> Back to Permissions
          </BackButton>
          <Title>Individual Permission Overrides</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Individuals"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton
              onClick={handleOpenAssignModal}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Override Individual Permission
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <LoadingSpinner />
            </Box>
          )}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
            <Typography
              sx={{
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {filteredEmployees.length}
              </Box>
              {filteredEmployees.length === 1 ? 'individual' : 'individuals'} with permission overrides
            </Typography>
          </Box>

          {!loading && filteredEmployees.length === 0 ? (
            <EmptyState
              title="No Permission Overrides Found"
              description={searchTerm
                ? "No individuals match your search criteria. Try adjusting your search terms."
                : "No individual permission overrides have been assigned yet. Start by overriding permissions for specific individuals."
              }
            />
          ) : (
            !loading && (
              <RolesGrid>
                {filteredEmployees.map((employeeOverride) => (
                  <RoleCard key={employeeOverride.employee.id}>
                    <RoleCardContent>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                        gap: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <EmployeeAvatar
                            employee={employeeOverride.employee}
                            size={48}
                          />
                          <Box>
                            <Typography
                              sx={{
                                color: COLORS.PRIMARY,
                                fontSize: '18px',
                                fontWeight: 600,
                                marginBottom: '2px'
                              }}
                            >
                              {employeeOverride.employee.fullName}
                            </Typography>
                            <Typography color="#666" variant="body2" sx={{ marginBottom: '4px' }}>
                              {employeeOverride.employee.email}
                            </Typography>
                            <Chip
                              label={`${employeeOverride.overrideCount || 0} Permission${employeeOverride.overrideCount !== 1 ? 's' : ''} Overridden`}
                              variant="outlined"
                              size="small"
                              sx={{
                                borderColor: '#FF9800',
                                color: '#FF9800',
                                backgroundColor: 'transparent',
                                fontWeight: 500,
                                fontSize: '12px',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <RoleDetailsGrid>
                        {employeeOverride.updatedAt && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Last Updated: {new Date(employeeOverride.updatedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </RoleDetailsGrid>

                      <RoleButtons>
                        <IconButton
                          onClick={() => handleNavigateToEmployee(employeeOverride.employee.id)}
                          color="primary"
                          size="small"
                          title="View/Edit permission overrides"
                        >
                          <FaEdit />
                        </IconButton>
                      </RoleButtons>
                    </RoleCardContent>
                  </RoleCard>
                ))}
              </RolesGrid>
            )
          )}
        </MainContainer>
      </Content>

      {/* Select Employee Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddOverride}
        title="Override Individual Permission"
        submitText="Continue"
        submitDisabled={!selectedEmployee}
        maxWidth="md"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Select Individual
            </Typography>
            <EmployeeSearch
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              label="Search for individual"
              placeholder="Type to search..."
              sx={{ width: '100%' }}
              height="56px"
              showSelectedAvatar={true}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              After selecting an employee, you'll be able to override specific permissions for them.
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EmployeePermissionOverrides;
